// src/features/media/components/MergeVideosModal.tsx

import { useState, useMemo, useCallback } from 'react';
import { Modal, Stack, Divider, Group, Text, Button, ScrollArea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranscodeVods } from '../useTranscodeVods';
import { useTags } from '@/features/tag/useTags';
import { useMergeVideos } from '../useVideoMutations';
import { MergeUtils, type MergeFormState } from '../MergeUtils';
import { MediaUtils } from '../MediaUtils';
import { MergeMetadataForm } from './MergeMetadataForm';
import { MergeSortableList, buildMergeListItems } from './MergeSortableList';
import type { VideoViewData } from '@/lib/types/Media';
import type { SharedLinkConfig } from '@/lib/types/link';

interface MergeVideosModalProps {
  opened: boolean;
  onClose: () => void;
  sourceVideoIds: number[];
  videosById: Map<number, VideoViewData>;
  linkConfig: SharedLinkConfig;
  onSuccess: (newVideo: VideoViewData, sourceCount: number, title: string) => void;
}

export function MergeVideosModal({
  opened,
  onClose,
  sourceVideoIds,
  videosById,
  linkConfig,
  onSuccess,
}: MergeVideosModalProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { data: transcodeVods, isLoading: vodsLoading } = useTranscodeVods();
  const { tags: allTags } = useTags();
  const mergeMutation = useMergeVideos();

  // Ordered list of media IDs (reorderable)
  const [orderedMediaIds, setOrderedMediaIds] = useState<number[]>(sourceVideoIds);

  // Form state initialized from source videos
  const [formState, setFormState] = useState<MergeFormState>(() => {
    const sourceVideos = sourceVideoIds
      .map(id => videosById.get(id))
      .filter((v): v is VideoViewData => v != null);
    return MergeUtils.buildInitialMergeState(sourceVideos, transcodeVods?.data);
  });

  // Auto-select highest quality when VODs load (if none selected yet)
  const vodsData = transcodeVods?.data;
  useMemo(() => {
    if (vodsData && vodsData.length > 0 && formState.transcodeVodId == null) {
      const sorted = [...vodsData].sort((a, b) => {
        const aMax = Math.max(...a.outputs.map(o => o.videoVerticalSize ?? 0));
        const bMax = Math.max(...b.outputs.map(o => o.videoVerticalSize ?? 0));
        return bMax - aMax;
      });
      setFormState(prev => ({ ...prev, transcodeVodId: sorted[0].transcodeVodId }));
    }
  }, [vodsData]);

  // Computed values
  const totalDuration = useMemo(
    () => MergeUtils.computeTotalDuration(orderedMediaIds, videosById),
    [orderedMediaIds, videosById]
  );

  const validation = useMemo(
    () => MergeUtils.validateMergeState(orderedMediaIds.length, formState.title, totalDuration),
    [orderedMediaIds.length, formState.title, totalDuration]
  );

  const listItems = useMemo(
    () => buildMergeListItems(orderedMediaIds, videosById, linkConfig),
    [orderedMediaIds, videosById, linkConfig]
  );

  // TranscodeVod options for Select
  const vodOptions = useMemo(() => {
    if (!vodsData) return [];
    return vodsData
      .map(vod => {
        const maxHeight = Math.max(...vod.outputs.map(o => o.videoVerticalSize ?? 0));
        return {
          value: vod.transcodeVodId.toString(),
          label: `${maxHeight}p - ${vod.label}`,
          maxHeight,
        };
      })
      .sort((a, b) => b.maxHeight - a.maxHeight);
  }, [vodsData]);

  // Handlers
  const handleFieldChange = useCallback(<K extends keyof MergeFormState>(
    field: K,
    value: MergeFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleReorder = useCallback((newOrder: number[]) => {
    setOrderedMediaIds(newOrder);
  }, []);

  const handleRemove = useCallback((mediaId: number) => {
    setOrderedMediaIds(prev => prev.filter(id => id !== mediaId));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!validation.isValid) return;

    const sourceCount = orderedMediaIds.length;
    const mergeTitle = formState.title.trim();
    const formattedDuration = MediaUtils.formatDuration(totalDuration);

    modals.openConfirmModal({
      title: 'Merge Videos?',
      children: (
        <Text size="sm">
          This will create a new video by combining {sourceCount} videos
          (total duration: {formattedDuration}). Continue?
        </Text>
      ),
      labels: { confirm: 'Merge Videos', cancel: 'Cancel' },
      onConfirm: () => {
        const baseVideo = videosById.get(orderedMediaIds[0])!;
        const req = MergeUtils.buildMergeRequest(baseVideo, formState, orderedMediaIds);

        mergeMutation.mutate(req, {
          onSuccess: (newVideo) => {
            onSuccess(newVideo, sourceCount, mergeTitle);
          },
        });
      },
    });
  }, [validation.isValid, orderedMediaIds, formState, totalDuration, videosById, mergeMutation, onSuccess]);

  const StatusIcon = validation.isValid ? IconCircleCheck : IconAlertCircle;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Merge Videos"
      size={900}
      fullScreen={isMobile}
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <MergeMetadataForm
          formState={formState}
          onFieldChange={handleFieldChange}
          vodOptions={vodOptions}
          vodsLoading={vodsLoading}
          allTags={allTags}
        />

        <Divider />

        <Text fw={600} size="sm">Merge Order</Text>
        <ScrollArea.Autosize mah="40vh">
          <MergeSortableList
            items={listItems}
            onReorder={handleReorder}
            onRemove={handleRemove}
          />
        </ScrollArea.Autosize>
      </Stack>

      {/* Footer: validation status + action buttons */}
      <Group justify="space-between" mt="lg" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
        <Group gap="xs">
          <StatusIcon size={16} color={`var(--mantine-color-${validation.statusColor}-6)`} />
          <Text size="sm" c={validation.statusColor}>
            {validation.statusMessage}
          </Text>
        </Group>

        <Group gap="sm">
          <Button variant="default" onClick={onClose} disabled={mergeMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={mergeMutation.isPending}
            disabled={!validation.isValid}
          >
            Merge Videos
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
